import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";

const SHARED = { name: "", category: "peptide", description: "", active: true, subcategory_ids: [] };
let seq = 0;
const newRow = () => ({ key: `new-${++seq}`, content: "", cost: "", price: "", on_sale: false, sale_price: "", stock: 0, low_stock_threshold: 5, recommended_bac_ml: 2, box_size: 10 });
const fromProduct = (p) => ({
  id: p.id, key: p.id, content: p.vial_size || "", cost: p.cost || "", price: p.price, on_sale: p.on_sale, sale_price: p.sale_price ?? "",
  stock: p.stock, low_stock_threshold: p.low_stock_threshold, recommended_bac_ml: p.recommended_bac_ml, box_size: p.box_size ?? 10,
});

const Field = ({ label, children }) => <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;

function ProfitLine({ r, testId }) {
  const cost = Number(r.cost) || 0;
  const selling = r.on_sale && r.sale_price !== "" ? Number(r.sale_price) : Number(r.price) || 0;
  const profit = selling - cost;
  const margin = selling > 0 ? (profit / selling) * 100 : 0;
  return (
    <div className="flex items-center justify-between rounded-md bg-background p-3 font-mono-data text-sm" data-testid={testId}>
      <span className="text-muted-foreground">{r.on_sale ? "Profit at sale price" : "Profit per unit"}</span>
      {cost > 0 && selling > 0
        ? <span className={profit < 0 ? "text-red-400" : margin < 20 ? "text-amber-400" : "text-emerald-400"}>${profit.toFixed(2)} · {margin.toFixed(1)}% margin</span>
        : <span className="text-xs text-muted-foreground">Enter cost and price to see profit</span>}
    </div>
  );
}

function ContentRow({ r, i, peptide, canRemove, onChange, onRemove }) {
  const set = (k) => (e) => onChange({ [k]: e.target.value });
  const t = (n) => `product-content-${i}-${n}`;
  return (
    <div className="space-y-4 rounded-md border border-border bg-muted/20 p-4" data-testid={`product-content-row-${i}`}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold">Content {i + 1}</p>
        {canRemove && <Button type="button" size="sm" variant="ghost" className="h-7 gap-1 text-red-400" onClick={onRemove} data-testid={t("remove")}><Trash2 className="h-3.5 w-3.5" />Remove</Button>}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Content (e.g. 10 mg)"><Input required value={r.content} onChange={set("content")} placeholder="e.g. 10 mg" data-testid={t("content-input")} /></Field>
        <Field label={r.id ? "Stock (use +/- in table)" : "Starting stock"}><Input type="number" min="0" value={r.stock} onChange={set("stock")} disabled={!!r.id} data-testid={t("stock-input")} /></Field>
        <Field label="Your cost ($ per unit)"><Input type="number" step="0.01" min="0" value={r.cost} onChange={set("cost")} placeholder="e.g. 18.00" data-testid={t("cost-input")} /></Field>
        <Field label="List price ($)"><Input required type="number" step="0.01" min="0" value={r.price} onChange={set("price")} placeholder="e.g. 45.00" data-testid={t("price-input")} /></Field>
      </div>
      <label className="flex items-center justify-between rounded-md border border-border p-3 text-sm">
        Put on sale <Switch checked={r.on_sale} onCheckedChange={(v) => onChange({ on_sale: v })} data-testid={t("on-sale-switch")} />
      </label>
      {r.on_sale && <Field label="Sale price ($): what clients pay while on sale"><Input required type="number" step="0.01" min="0" value={r.sale_price} onChange={set("sale_price")} data-testid={t("sale-price-input")} /></Field>}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Field label="Low-stock alert at"><Input type="number" min="0" value={r.low_stock_threshold} onChange={set("low_stock_threshold")} data-testid={t("threshold-input")} /></Field>
        <Field label="Vials per box"><Input type="number" min="1" value={r.box_size} onChange={set("box_size")} data-testid={t("box-size-input")} /></Field>
        {peptide && <Field label="Recommended BAC (mL)"><Input type="number" step="0.1" min="0" value={r.recommended_bac_ml} onChange={set("recommended_bac_ml")} data-testid={t("bac-ml-input")} /></Field>}
      </div>
      <ProfitLine r={r} testId={t("margin-preview")} />
    </div>
  );
}

const toBody = (shared, r, gid) => ({
  ...shared, vial_size: r.content.trim(), price: Number(r.price), cost: r.cost === "" ? 0 : Number(r.cost),
  on_sale: r.on_sale, sale_price: r.sale_price === "" ? null : Number(r.sale_price),
  stock: Number(r.stock), low_stock_threshold: Number(r.low_stock_threshold), box_size: Number(r.box_size) || 10,
  recommended_bac_ml: shared.category === "peptide" ? Number(r.recommended_bac_ml) : 0, group_id: gid || null,
});

function SubcategoryPicker({ all, value, onChange }) {
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="space-y-1.5">
      <Label>Sub-categories (pick any)</Label>
      {all.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="product-no-subcategories">No sub-categories yet. Add them from Inventory → Sub-categories.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {all.map((s) => (
            <button key={s.id} type="button" onClick={() => toggle(s.id)} data-testid={`product-subcategory-${s.id}`}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${value.includes(s.id) ? "border-emerald-500 bg-emerald-500/10 text-emerald-300" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {s.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ProductFormDialog({ group, open, onClose, onSaved }) {
  const [subcats, setSubcats] = useState([]);
  const [shared, setShared] = useState(SHARED);
  const [rows, setRows] = useState([newRow()]);
  const [removed, setRemoved] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const g = group?.length ? group : null;
    setShared(g ? { name: g[0].name, category: g[0].category, description: g[0].description || "", active: g[0].active, subcategory_ids: g[0].subcategory_ids || [] } : SHARED);
    setRows(g ? g.map(fromProduct) : [newRow()]);
    setRemoved([]);
    api.get("/admin/subcategories").then((r) => setSubcats(r.data)).catch(() => {});
  }, [open, group]);

  const patchRow = (key, patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const removeRow = (r) => {
    setRows((rs) => rs.filter((x) => x.key !== r.key));
    if (r.id) setRemoved((ids) => [...ids, r.id]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    let gid = group?.[0]?.group_id;
    try {
      for (const r of rows) {
        const body = toBody(shared, r, gid);
        const res = r.id ? await api.put(`/admin/products/${r.id}`, body) : await api.post("/admin/products", body);
        gid = gid || res.data.group_id;
        if (!r.id) patchRow(r.key, { id: res.data.id });
      }
      for (const id of removed) await api.delete(`/admin/products/${id}`);
      toast.success("Product saved");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
      onSaved();
    } finally {
      setBusy(false);
    }
  };

  const setS = (k) => (e) => setShared({ ...shared, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="product-form-dialog">
        <DialogHeader><DialogTitle>{group ? "Edit product" : "Add product"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Field label="Name (e.g. Reta)"><Input required value={shared.name} onChange={setS("name")} data-testid="product-name-input" /></Field></div>
          <div className="col-span-2">
            <Field label="Category">
              <Select value={shared.category} onValueChange={(v) => setShared({ ...shared, category: v })}>
                <SelectTrigger data-testid="product-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="peptide">Peptide</SelectItem><SelectItem value="bac_water">BAC Water</SelectItem><SelectItem value="supply">Supply</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          {shared.category === "peptide" && (
            <div className="col-span-2"><SubcategoryPicker all={subcats} value={shared.subcategory_ids} onChange={(v) => setShared({ ...shared, subcategory_ids: v })} /></div>
          )}
          <div className="col-span-2 space-y-3">
            <p className="text-sm text-muted-foreground">Add each content you sell. Clients pick one before adding to cart, and each has its own price, cost and stock.</p>
            {rows.map((r, i) => (
              <ContentRow key={r.key} r={r} i={i} peptide={shared.category === "peptide"} canRemove={rows.length > 1}
                onChange={(p) => patchRow(r.key, p)} onRemove={() => removeRow(r)} />
            ))}
            <Button type="button" variant="outline" className="w-full gap-2" onClick={() => setRows((rs) => [...rs, newRow()])} data-testid="product-add-content-button">
              <Plus className="h-4 w-4" />Add another content
            </Button>
            {removed.length > 0 && <p className="text-xs text-amber-400" data-testid="product-removed-note">{removed.length} content option(s) will be deleted when you save. Past orders keep their details.</p>}
          </div>
          <div className="col-span-2"><Field label="Description"><Textarea value={shared.description} onChange={setS("description")} data-testid="product-description-input" /></Field></div>
          <label className="col-span-2 flex items-center justify-between rounded-md border border-border p-3 text-sm">
            Visible in client shop <Switch checked={shared.active} onCheckedChange={(v) => setShared({ ...shared, active: v })} data-testid="product-active-switch" />
          </label>
          <Button type="submit" className="col-span-2" disabled={busy} data-testid="product-save-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save product</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
